<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const items = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();

const PALETTE = [
  '#0EA5E9',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#84CC16',
  '#10B981',
  '#14B8A6',
  '#64748B',
  '#E30611',
];

const dialogRef = ref(null);
const editingIndex = ref(-1);
const form = ref({
  label: '',
  description: '',
  color: PALETTE[0],
  enabled: true,
});

const isLabelTaken = computed(() =>
  items.value.some(
    (item, index) =>
      index !== editingIndex.value &&
      item.label.trim().toLowerCase() === form.value.label.trim().toLowerCase()
  )
);

const canSave = computed(
  () =>
    form.value.label.trim() &&
    form.value.description.trim() &&
    !isLabelTaken.value
);

const enabledCount = computed(
  () => items.value.filter(item => item.enabled).length
);

const slugify = value =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-zа-яё0-9-]/gi, '');

const openAdd = () => {
  editingIndex.value = -1;
  form.value = {
    label: '',
    description: '',
    color: PALETTE[items.value.length % PALETTE.length],
    enabled: true,
  };
  dialogRef.value?.open();
};

const openEdit = index => {
  editingIndex.value = index;
  form.value = { ...items.value[index] };
  dialogRef.value?.open();
};

const save = () => {
  if (!canSave.value) return;
  const entry = {
    ...form.value,
    label: slugify(form.value.label),
    description: form.value.description.trim(),
  };
  if (editingIndex.value === -1) {
    items.value = [...items.value, { ...entry, id: entry.id || entry.label }];
  } else {
    items.value = items.value.map((item, index) =>
      index === editingIndex.value ? entry : item
    );
  }
  dialogRef.value?.close();
};

const remove = index => {
  items.value = items.value.filter((_, i) => i !== index);
};

const setEnabled = (index, value) => {
  items.value = items.value.map((item, i) =>
    i === index ? { ...item, enabled: value } : item
  );
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-3">
      <span class="text-xs text-n-slate-11">
        {{
          t('AI_MARKUP.AUTOTAGS.COUNT', {
            enabled: enabledCount,
            total: items.length,
          })
        }}
      </span>
      <Button
        :label="t('AI_MARKUP.AUTOTAGS.ADD')"
        icon="i-lucide-plus"
        size="sm"
        variant="faded"
        @click="openAdd"
      />
    </div>
    <div class="overflow-hidden border rounded-lg border-n-weak">
      <table class="w-full text-sm border-collapse table-fixed">
        <thead>
          <tr
            class="bg-n-alpha-1 text-start text-[11px] uppercase tracking-wide text-n-slate-10"
          >
            <th class="w-44 px-3 py-2 font-medium text-start">
              {{ t('AI_MARKUP.AUTOTAGS.LABEL') }}
            </th>
            <th class="px-3 py-2 font-medium text-start">
              {{ t('AI_MARKUP.AUTOTAGS.DESCRIPTION') }}
            </th>
            <th class="w-16 px-3 py-2 font-medium text-center">
              {{ t('AI_MARKUP.AUTOTAGS.ENABLED') }}
            </th>
            <th class="w-20 px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.id || item.label"
            class="border-t border-n-weak group hover:bg-n-alpha-1"
            :class="{ 'opacity-60': !item.enabled }"
          >
            <td class="px-3 py-2 align-top">
              <span
                class="inline-flex items-center max-w-full gap-1.5 h-6 px-2 rounded-md border border-n-weak bg-n-solid-2 text-xs font-medium text-n-slate-12"
              >
                <span
                  class="flex-shrink-0 rounded-full size-2"
                  :style="{ background: item.color }"
                />
                <span class="truncate">{{ item.label }}</span>
              </span>
            </td>
            <td class="px-3 py-2 text-xs leading-5 align-top text-n-slate-11">
              {{ item.description }}
            </td>
            <td class="px-3 py-2 text-center align-top">
              <Switch
                :model-value="item.enabled"
                class="mt-1"
                @update:model-value="value => setEnabled(index, value)"
              />
            </td>
            <td class="px-2 py-1.5 align-top">
              <div
                class="flex justify-end gap-0.5 opacity-60 group-hover:opacity-100"
              >
                <Button
                  v-tooltip.top="t('AI_MARKUP.EDIT')"
                  icon="i-lucide-pencil"
                  size="xs"
                  variant="ghost"
                  color="slate"
                  @click="openEdit(index)"
                />
                <Button
                  v-tooltip.top="t('AI_MARKUP.DELETE')"
                  icon="i-lucide-trash-2"
                  size="xs"
                  variant="ghost"
                  color="ruby"
                  @click="remove(index)"
                />
              </div>
            </td>
          </tr>
          <tr v-if="!items.length">
            <td
              colspan="4"
              class="px-3 py-6 text-sm text-center text-n-slate-10"
            >
              {{ t('AI_MARKUP.AUTOTAGS.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Dialog
      ref="dialogRef"
      :title="
        editingIndex === -1
          ? t('AI_MARKUP.AUTOTAGS.ADD_TITLE')
          : t('AI_MARKUP.AUTOTAGS.EDIT_TITLE')
      "
      :confirm-button-label="t('AI_MARKUP.SAVE_ITEM')"
      :disable-confirm-button="!canSave"
      width="lg"
      @confirm="save"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.label"
          :label="t('AI_MARKUP.AUTOTAGS.LABEL')"
          :placeholder="t('AI_MARKUP.AUTOTAGS.LABEL_PLACEHOLDER')"
          :message="
            isLabelTaken
              ? t('AI_MARKUP.AUTOTAGS.LABEL_TAKEN')
              : t('AI_MARKUP.AUTOTAGS.LABEL_HINT')
          "
          :message-type="isLabelTaken ? 'error' : 'info'"
        />
        <TextArea
          v-model="form.description"
          :label="t('AI_MARKUP.AUTOTAGS.DESCRIPTION')"
          :placeholder="t('AI_MARKUP.AUTOTAGS.DESCRIPTION_PLACEHOLDER')"
          :max-length="500"
          min-height="5rem"
          show-character-count
        />
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('AI_MARKUP.AUTOTAGS.COLOR') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="color in PALETTE"
              :key="color"
              type="button"
              class="rounded-full size-6 ring-offset-2 ring-offset-n-solid-1 transition-shadow"
              :class="
                form.color === color
                  ? 'ring-2 ring-n-slate-12'
                  : 'hover:ring-2 hover:ring-n-slate-6'
              "
              :style="{ background: color }"
              @click="form.color = color"
            />
          </div>
        </div>
        <label
          class="flex items-center justify-between gap-3 text-sm text-n-slate-12"
        >
          {{ t('AI_MARKUP.AUTOTAGS.ENABLED_LONG') }}
          <Switch v-model="form.enabled" />
        </label>
      </div>
    </Dialog>
  </div>
</template>
